<template>
  <div v-if="show" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Email Confirmation Required</h2>
        <button @click="cancel" class="close-btn" aria-label="Close modal">
          <span>&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <p>
          Please provide your email address to complete the sign-in process.
        </p>
        <div class="form-group">
          <label for="confirmEmail">Email Address</label>
          <input
            id="confirmEmail"
            v-model="emailInput"
            type="email"
            placeholder="Enter your email address"
            @keyup.enter="confirmEmail"
            ref="emailInputRef"
            required
          />
        </div>
      </div>
      <div class="modal-footer">
        <button @click="cancel" class="btn btn-secondary">Cancel</button>
        <button
          @click="confirmEmail"
          class="btn btn-primary"
          :disabled="!isValidEmail"
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["confirm", "cancel"]);

const emailInput = ref("");
const emailInputRef = ref(null);

const isValidEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailInput.value);
});

const confirmEmail = () => {
  if (isValidEmail.value) {
    emit("confirm", emailInput.value);
  }
};

const cancel = () => {
  emit("cancel");
};

const handleOverlayClick = () => {
  // Don't close on overlay click - require explicit cancel
};

// Focus the input when modal opens
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      nextTick(() => {
        emailInputRef.value?.focus();
      });
    } else {
      emailInput.value = "";
    }
  }
);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e1e5e9;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body p {
  margin: 0 0 1rem 0;
  color: #6c757d;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #42b883;
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e1e5e9;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
}

.btn-primary {
  background-color: #42b883;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #3aa876;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  color: #495057;
  background-color: #f8f9fa;
}

.close-btn span {
  line-height: 1;
  font-weight: 300;
}
</style>
